import React, { useState, useMemo } from 'react';
import { ListOrdered, Copy, Check, ChevronDown, ChevronUp, BookMarked } from 'lucide-react';
import DOMPurify from 'dompurify';

interface BookTableOfContentsCardProps {
  tableOfContents?: string | null;
  bookTitle?: string;
  className?: string;
  defaultExpanded?: boolean;
  maxCollapsedLength?: number;
  showCopy?: boolean;
}

export const BookTableOfContentsCard: React.FC<BookTableOfContentsCardProps> = ({
  tableOfContents,
  bookTitle,
  className = '',
  defaultExpanded = true,
  maxCollapsedLength = 300,
  showCopy = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  // Parse HTML or plain text
  const { isHtml, rawPlainText, sanitizedHtml } = useMemo(() => {
    if (!tableOfContents || !tableOfContents.trim()) {
      return { isHtml: false, rawPlainText: '', sanitizedHtml: '' };
    }

    const trimmed = tableOfContents.trim();
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(trimmed);

    if (hasHtmlTags) {
      const cleanHtml = DOMPurify.sanitize(trimmed, {
        ALLOWED_TAGS: [
          'p',
          'b',
          'i',
          'u',
          's',
          'strong',
          'em',
          'strike',
          'ul',
          'ol',
          'li',
          'h3',
          'h4',
          'blockquote',
          'br',
          'span',
        ],
        ALLOWED_ATTR: ['class'],
      });

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanHtml;
      const text = tempDiv.textContent || tempDiv.innerText || '';

      return {
        isHtml: true,
        rawPlainText: text.trim(),
        sanitizedHtml: cleanHtml,
      };
    }

    return {
      isHtml: false,
      rawPlainText: trimmed,
      sanitizedHtml: '',
    };
  }, [tableOfContents]);

  // Plain text lines
  const plainLines = useMemo(() => {
    if (!rawPlainText) return [];
    return rawPlainText.split(/\r?\n/).filter(Boolean);
  }, [rawPlainText]);

  // Utility to parse a line into Title and Page if applicable (e.g. "Bab 1: Pendahuluan (hal. 12)")
  const parsedLines = useMemo(() => {
    return plainLines.map((line) => {
      const trimmed = line.trim();
      
      // Match something like "Bab 1: Pendahuluan (hal. 1)" or "Bab 1: Pendahuluan hal. 1" or "Bab 1: Pendahuluan (hal 1-12)"
      const pageRegex = /(.*?)\s*[-•.]?\s*\(?\b(?:hal|hal\.|page|p|p\.|h|h\.)\s*(\d+(?:\s*-\s*\d+)?)\)?\s*$/i;
      const match = trimmed.match(pageRegex);
      
      if (match) {
        return {
          title: match[1].trim().replace(/\s*[.•-]+\s*$/, ''), // Clean trailing dots/dashes
          page: `hal. ${match[2]}`,
          hasPage: true,
        };
      }
      return {
        title: trimmed,
        page: '',
        hasPage: false,
      };
    });
  }, [plainLines]);

  if (!rawPlainText) {
    return null; // Don't show empty placeholder unless desired
  }

  const isLong = rawPlainText.length > maxCollapsedLength;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(rawPlainText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-slate-50/70 to-emerald-50/20 border border-slate-200/90 shadow-2xs transition-all duration-200 ${className}`}
    >
      {/* Decorative subtle background icon */}
      <div className="absolute right-2 -top-1 pointer-events-none opacity-[0.05] text-emerald-900 select-none">
        <BookMarked className="w-20 h-20" />
      </div>

      {/* Header bar */}
      <div className="px-4 py-3 border-b border-slate-100/90 flex items-center justify-between gap-3 bg-white/70 backdrop-blur-xs">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200/60 text-emerald-700 flex items-center justify-center shrink-0 shadow-2xs">
            <ListOrdered className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider truncate">
              Daftar Isi Buku (Table of Contents)
            </h4>
          </div>
        </div>

        {/* Action pills */}
        <div className="flex items-center gap-1.5 shrink-0">
          {showCopy && (
            <button
              type="button"
              onClick={handleCopy}
              title="Salin Daftar Isi"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200 text-[11px] font-medium transition cursor-pointer shadow-2xs"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                  <span className="text-emerald-700 font-semibold text-[10px]">Tersalin</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="text-[10px]">Salin</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content body */}
      <div className="p-4 sm:p-4.5">
        <div
          className={`border-l-2 border-emerald-400/80 pl-3.5 sm:pl-4 transition-all duration-200 ${
            !isExpanded && isLong ? 'max-h-40 overflow-hidden relative' : ''
          }`}
        >
          {isHtml ? (
            <div
              className="prose prose-sm sm:prose-base max-w-none text-slate-700 leading-relaxed sm:leading-7 2xl:leading-8 font-normal text-sm sm:text-base lg:text-[16px] 2xl:text-[17.5px]
                [&>p]:mb-2 [&>p:last-child]:mb-0
                [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-2.5 [&>ul]:space-y-1.5
                [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-2.5 [&>ol]:space-y-1.5
                [&>li]:text-slate-700
                [&>strong]:text-slate-900 [&>b]:text-slate-900"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <div className="space-y-2 font-sans text-sm sm:text-base lg:text-[16px] 2xl:text-[17.5px] text-slate-800">
              {parsedLines.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-3 py-1.5 group/line hover:bg-slate-100/50 -mx-2 px-2 rounded-lg transition duration-150"
                >
                  <span className="font-medium text-slate-700 break-words flex-1 leading-relaxed text-justify text-sm sm:text-base lg:text-[16px]">
                    {item.title}
                  </span>
                  {item.hasPage ? (
                    <>
                      {/* Dotted alignment leader for a professional book layout */}
                      <span className="hidden sm:inline-block border-b border-dotted border-slate-300 flex-1 mx-2 relative top-[-4px] min-w-[24px] self-stretch" />
                      <span className="font-mono text-xs sm:text-sm text-slate-600 shrink-0 select-none bg-slate-100/90 px-2.5 py-0.5 rounded-md border border-slate-200/60 font-semibold self-center">
                        {item.page}
                      </span>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {/* Fade gradient overlay when collapsed */}
          {!isExpanded && isLong && (
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 via-slate-50/80 to-transparent pointer-events-none" />
          )}
        </div>

        {/* Expand / Collapse Toggle Button */}
        {isLong && (
          <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 transition cursor-pointer"
            >
              <span>{isExpanded ? 'Tampilkan Lebih Ringkas' : 'Lihat Seluruh Daftar Isi'}</span>
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
